import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type Props = {
  audiences: AudiencesModel[];
  connections: ConnectionModel[];
  lists?: {
    id: string;
    name: string;
  }[];
  validConnection: (
    isAllContactsSync: boolean,
    connection: Partial<ConnectionModel>,
  ) => boolean;
  connectionTemplate: (
    extra?: Partial<ConnectionModel>,
  ) => Partial<ConnectionModel>;
};

export const useManageConnection = ({
  connections,
  audiences,
  lists,
  validConnection,
  connectionTemplate,
}: Props) => {
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const [allConnections, setAllConnections] =
    useState<Partial<ConnectionModel>[]>(connections);
  const [isAllContactsSync, setIsAllContactsSync] = useState(
    !allConnections.find(conn => conn.active)?.audienceId,
  );

  useEffect(() => {
    setAllConnections(connections);
  }, [connections]);

  const selectedLists = useMemo(
    () =>
      allConnections.reduce((acc, conn) => {
        if (conn.data?.listId && (!conn.id || conn.audienceId)) {
          acc[conn.data.listId] = true;
        }
        return acc;
      }, {}),
    [allConnections],
  );
  const selectedAudiences = useMemo(
    () =>
      allConnections.reduce((acc, conn) => {
        if (conn.audienceId) {
          acc[conn.audienceId] = true;
        }
        return acc;
      }, {}),
    [allConnections],
  );
  const selectableLists = (conn?: Partial<ConnectionModel>) =>
    lists
      ?.filter(
        list => list.id === conn?.data?.listId || !selectedLists[list.id],
      )
      .map(l => ({ id: l.id, name: l.name })) || [];
  const selectableAudiences = (conn?: Partial<ConnectionModel>) =>
    audiences.filter(
      aud => aud.id === conn?.audienceId || !selectedAudiences[aud.id],
    );

  const { update, create, deleteConnection } = useConnectionCRUDActions();

  const addConnection = () => {
    setAllConnections(prevAllConnections => [
      ...prevAllConnections,
      connectionTemplate(),
    ]);
  };
  const remove = async (index: number) => {
    const connection = allConnections[index];
    if (connection.id) {
      await deleteConnection(activeFbPageId, connection as ConnectionModel);
    }
    setAllConnections(prevAllConnections =>
      prevAllConnections.filter(conn => conn !== connection),
    );
  };

  const updateOrEdit = async (
    index: number,
    updateData: Partial<ConnectionModel>,
  ) => {
    const connection = allConnections[index];
    if (connection.id) {
      const updatedConnection = await update(activeFbPageId, {
        id: connection.id,
        ...updateData,
      });
      if (updatedConnection) {
        setAllConnections(prevAllConnections =>
          Object.assign([], prevAllConnections, { [index]: updatedConnection }),
        );
      }
    } else if (
      validConnection(isAllContactsSync, { ...connection, ...updateData })
    ) {
      const createdConnection = await create(activeFbPageId, {
        ...connection,
        ...updateData,
      });
      if (createdConnection) {
        setAllConnections(prevAllConnections =>
          Object.assign([], prevAllConnections, { [index]: createdConnection }),
        );
      }
    } else {
      setAllConnections(prevAllConnections =>
        Object.assign([], prevAllConnections, {
          [index]: { ...connection, ...updateData },
        }),
      );
    }
  };

  const onChangeType = (isAllContacts: boolean) => {
    setIsAllContactsSync(isAllContacts);
  };

  return {
    allConnections,
    setAllConnections,
    isAllContactsSync,
    selectableLists,
    selectableAudiences,
    addConnection,
    remove,
    updateOrEdit,
    onChangeType,
  };
};
